// @ts-nocheck
/**
 * Recipe search core, shared by the search overlay (assets/vhp-search.js), the Explore tab
 * (assets/vhp-explore.js) and the planner's own searches (assets/vhp-planner-funnel.js).
 * No DOM except reading the categories JSON (snippets/vhp-search.liquid).
 *
 * Every search goes to the Algolia `rezepte` index (typo tolerance, German plurals, ranking).
 * Algolia does not split German compounds ("tofu" misses "200 g Räuchertofu"), and the index keeps
 * ingredients as raw lines with amounts. So the full record list is loaded once (160 recipes, cached
 * for the session) to build an ingredient dictionary: "Tofu" = every raw line that contains "tofu".
 * An ingredient filter is then sent to Algolia as facetFilters over those raw lines (OR inside one
 * ingredient, AND across ingredients), and a typed word that is an ingredient also searches that way.
 * Meal-plan exclusive recipes only show for subscribers. The index does not carry the flag, so the ids come from
 * the theme (templates/collection.recipe-facts.liquid); a flag or tag in the index still counts. The other facts the
 * index lacks come from there too: row.slot, carbs, portions, pub (YYYY-MM-DD), tags (lowercase), for the filters
 * (assets/vhp-filters.js) and the "Neueste" sort, and row.cols (collection handles) for the Explore counts.
 *
 *   vhpSearchCore.suggest(q)           -> { recipes, ingredients, categories } for the typeahead
 *   vhpSearchCore.search(state)        -> { rows, mode, have } ; state: { q, ings, protein, time, kcal }
 *   vhpSearchCore.ids(q) / peekIds(q)  -> Map productId -> rank (planner)
 *   vhpSearchCore.recents              -> recent searches (queries, ingredients, categories, recipes)
 */
(() => {
  'use strict';

  if (window.vhpSearchCore) return;

  const APP_ID = 'IL1XX24PD6';
  const SEARCH_KEY = 'bd476f100f29d746663ea773c3297f86';
  const INDEX = 'rezepte';
  const DATA_KEY = 'vhp_search_data_v6';
  const FACTS_URL = '/collections/all?view=recipe-facts';
  const DATA_TTL = 3600 * 1000;
  const RECENT_KEY = 'vhp_search_recent_v1';
  const LEGACY_RECENT_KEY = 'vhp_explore_recent';
  const MAX_RECENT = 12;
  const MAX_CACHE = 60;
  const ATTRS = ['handle', 'title', 'image', 'protein_g', 'kcal', 'zeit_min'];

  /* ---------------------------------------------------------------- text */

  // Lowercase, no accents: "Käse" and "kase" match, "ß" becomes "ss".
  const norm = (value) =>
    String(value == null ? '' : value)
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/ß/g, 'ss');

  // Cheap German plural / case ending: "linsen" -> "lins", "nudeln" -> "nudel".
  const stem = (token) => (token.length > 4 ? token.replace(/(en|n|e|s)$/, '') : token);

  const words = (q) => norm(q).split(/[\s,+]+/).filter(Boolean);

  const num = (value) => {
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  };

  const pidOf = (id) => (String(id || '').match(/(\d+)$/) || [])[1] || '';

  const isExclusive = (hit) => {
    if (hit.meal_plan_exclusive === true || hit.meal_plan_exclusive === 'true') return true;
    const tags = hit.tags || hit._tags;
    return Array.isArray(tags) && tags.some((tag) => String(tag).toLowerCase() === 'meal-plan-exclusive');
  };

  // Subscribers see the meal-plan exclusive recipes (snippets/vhp-search.liquid, or the app shell config).
  const isSubscriber = () => {
    const flag = document.querySelector('[data-vhp-search]')?.dataset.subscriber;
    if (flag) return flag === 'true';
    try {
      return Boolean(JSON.parse(document.getElementById('vhp-account-config')?.textContent || '{}').subscriber);
    } catch (_) {
      return false;
    }
  };

  /* ---------------------------------------------------------------- Algolia */

  const encode = (params) =>
    Object.keys(params)
      .filter((key) => params[key] !== undefined)
      .map((key) => {
        const value = params[key];
        return `${key}=${encodeURIComponent(typeof value === 'string' ? value : JSON.stringify(value))}`;
      })
      .join('&');

  const multi = (requests) =>
    fetch(`https://${APP_ID}-dsn.algolia.net/1/indexes/*/queries`, {
      method: 'POST',
      headers: { 'X-Algolia-Application-Id': APP_ID, 'X-Algolia-API-Key': SEARCH_KEY },
      body: JSON.stringify({ requests: requests.map((params) => ({ indexName: INDEX, params: encode(params) })) }),
    }).then((response) => {
      if (!response.ok) throw new Error(`algolia ${response.status}`);
      return response.json().then((data) => data.results || []);
    });

  /* ---------------------------------------------------------------- ingredient names */

  const UNIT =
    /^(?:g|kg|mg|ml|l|cl|dl|el|tl|msp|prise|prisen|stuck|stk|dose|dosen|scheibe|scheiben|tropfen|packung|packungen|pck|packchen|bund|handvoll|zehe|zehen|becher|tasse|tassen|glas|cm|blatt|blatter|zweig|zweige|spritzer|schuss|portion|portionen|kugel|kugeln|stange|stangen|knolle|knollen|beutel|riegel|messerspitze)\.?$/;
  // Words that describe an ingredient but are not one ("Veganes", "gemahlen", "natur" ...).
  const NOT_INGREDIENT =
    /^(?:vegan|frisch|natur|gross|klein|mittel|braun|weiss|rot|grun|gelb|schwarz|ganz|gemahlen|gehackt|getrocknet|gerauchert|geroestet|gerostet|ofengerostet|ungesusst|gesusst|fest|weich|tiefgekuhlt|tk|bio|light|extra|fein|grob|express|original|style|alternativ|pflanzlich|optional|naturbelassen|gewurfelt|geschnitten|gekocht|ungekocht|roh|reif|kalt|warm|heiss|lauwarm|mittelgross|halb|vollkorn|instant|zuckerfrei|glutenfrei|fettarm|ohne|nach|belieben|geschmack|sorte|vanille|schoko|mischung|vetain|alpro|oatly|ben|uncle|kolln|rewe|edeka|dm|ja|gut|best|lecker)(?:e|er|es|en|em|s)?$/;

  // The key as an ingredient inside a line: at a word start ("Reis", "Reisnudeln") or as the end of a
  // compound ("Räuchertofu", "Kidneybohnen"), not somewhere inside a word ("Preiselbeeren").
  const contains = (n, key) => {
    let at = n.indexOf(key);
    while (at !== -1) {
      if (at === 0 || !/[a-z]/.test(n[at - 1])) return true;
      if (/^[a-z]{0,2}(?![a-z])/.test(n.slice(at + key.length))) return true;
      at = n.indexOf(key, at + 1);
    }
    return false;
  };

  // "200 g Tofu, fest, Natur" -> ["Tofu"], "15 g Erdnussbutter/Erdnussmus" -> both.
  const cleanNames = (raw) => {
    let s = String(raw || '')
      .replace(/\([^)]*\)/g, ' ')
      .split(/,|;|\s(?:mit|oder|für|fur|zum|zur|nach|z\.\s?b\.|ca\.)\s/i)[0];
    const parts = s.split(/\s+/).filter(Boolean);
    while (parts.length && (/^[\d.,/½¼¾⅓⅔–-]+$/.test(parts[0]) || /^ca\.?$/i.test(parts[0]) || UNIT.test(norm(parts[0])))) {
      parts.shift();
    }
    s = parts.join(' ').replace(/^[-–:]+/, '').trim();
    if (!/[a-zäöü]/i.test(s)) return [];
    return s
      .split('/')
      .map((part) => part.trim())
      .filter((part) => part.length > 1 && part.length <= 40);
  };

  /* ---------------------------------------------------------------- data (records + dictionary) */

  let data = null;
  let dataLoading = null;

  const toRecord = (hit) => ({
    id: String(hit.objectID || ''),
    pid: pidOf(hit.objectID),
    handle: hit.handle,
    title: hit.title,
    image: hit.image || '',
    protein: num(hit.protein_g),
    kcal: num(hit.kcal),
    time: num(hit.zeit_min),
    zutaten: Array.isArray(hit.zutaten) ? Array.from(new Set(hit.zutaten.map(String))) : [],
    exclusive: isExclusive(hit),
  });

  // Meal-plan exclusive product ids and the per-product facts, page by page. Null on failure (not cached then).
  const loadFacts = async () => {
    const ids = [];
    const recipes = {};
    try {
      for (let page = 1; page; ) {
        const response = await fetch(`${FACTS_URL}&page=${page}`, { credentials: 'same-origin' });
        if (!response.ok) return null;
        const body = await response.json();
        ids.push(...(body.ids || []).map(String));
        Object.assign(recipes, body.recipes || {});
        page = body.next || 0;
      }
    } catch (_) {
      return null;
    }
    return { ids, recipes };
  };

  const buildData = (all, extra) => {
    const flagged = new Set(extra?.ids || []);
    const facts = extra?.recipes || {};
    all.forEach((row) => {
      if (flagged.has(row.pid)) row.exclusive = true;
      const f = facts[row.pid] || {};
      row.slot = typeof f.s === 'string' ? f.s : 'main';
      row.carbs = num(f.c);
      row.portions = num(f.p);
      row.pub = f.pub || '';
      row.tags = String(f.t || '').split('|').filter(Boolean);
      row.cols = String(f.k || '').split('|').filter(Boolean);
    });
    const rows = isSubscriber() ? all : all.filter((row) => !row.exclusive);
    const byId = new Map();
    const byHandle = new Map();
    const byPid = new Map();
    rows.forEach((row) => {
      row.zn = row.zutaten.map(norm);
      row.titleN = norm(row.title);
      byId.set(row.id, row);
      byHandle.set(row.handle, row);
      if (row.pid) byPid.set(row.pid, row);
    });
    // Raw lines (exact facet values) with their normalized form.
    const raws = new Map();
    rows.forEach((row) => row.zutaten.forEach((raw, i) => raws.set(raw, row.zn[i])));

    // Ingredient candidates: the cleaned name of each line, plus nouns inside multi-word names
    // ("Soja-Joghurt Natur" also gives "Joghurt", "Veganes Proteinpulver" gives "Proteinpulver").
    const cands = new Map();
    const add = (name) => {
      const key = norm(name).replace(/\s+/g, ' ').trim();
      if (key.length < 3 || NOT_INGREDIENT.test(key)) return;
      const prev = cands.get(key);
      if (!prev) cands.set(key, { key, name, seen: 1 });
      else prev.seen += 1;
    };
    raws.forEach((_, raw) => {
      cleanNames(raw).forEach((name) => {
        add(name);
        const pieces = name.split(/[\s-]+/).filter(Boolean);
        if (pieces.length > 1) {
          pieces.forEach((piece) => {
            if (piece.length >= 4 && /^[A-ZÄÖÜ]/.test(piece)) add(piece);
          });
        }
      });
    });
    data = { rows, byId, byHandle, byPid, raws, cands: Array.from(cands.values()), counts: new Map(), families: new Map() };
    return data;
  };

  const loadData = () => {
    if (data) return Promise.resolve(data);
    if (dataLoading) return dataLoading;
    try {
      const cached = JSON.parse(sessionStorage.getItem(DATA_KEY) || 'null');
      if (cached && Date.now() - cached.at < DATA_TTL && Array.isArray(cached.rows) && Array.isArray(cached.exclusive?.ids) && cached.exclusive.recipes) {
        return Promise.resolve(buildData(cached.rows, cached.exclusive));
      }
    } catch (_) {}
    dataLoading = Promise.all([
      multi([
        { query: '', hitsPerPage: 1000, attributesToRetrieve: ATTRS.concat('zutaten', 'meal_plan_exclusive', 'tags', '_tags'), attributesToHighlight: [] },
      ]),
      loadFacts(),
    ])
      .then(([[result], exclusive]) => {
        const seen = new Set();
        const rows = (result.hits || [])
          .filter((hit) => hit.handle && hit.title && !seen.has(hit.handle) && seen.add(hit.handle))
          .map(toRecord);
        if (exclusive) {
          try {
            sessionStorage.setItem(DATA_KEY, JSON.stringify({ at: Date.now(), rows, exclusive }));
          } catch (_) {}
        }
        return buildData(rows, exclusive);
      })
      .catch((error) => {
        dataLoading = null;
        throw error;
      });
    return dataLoading;
  };

  // Every raw line containing the key: the OR group sent to Algolia for one ingredient.
  const family = (key) => {
    if (!data) return [];
    if (!data.families.has(key)) {
      const list = [];
      data.raws.forEach((n, raw) => {
        if (contains(n, key)) list.push(raw);
      });
      data.families.set(key, list);
    }
    return data.families.get(key);
  };

  // Recipes containing the ingredient (local, for the counts next to suggestions).
  const countFor = (key) => {
    if (!data) return 0;
    if (!data.counts.has(key)) {
      data.counts.set(key, data.rows.filter((row) => row.zn.some((n) => contains(n, key))).length);
    }
    return data.counts.get(key);
  };

  const ingredient = (key) => {
    const k = norm(key).trim();
    const cand = data?.cands.find((c) => c.key === k);
    return { key: k, name: cand ? cand.name : String(key).trim().replace(/^./, (c) => c.toUpperCase()), count: countFor(k) };
  };

  // Ingredients for a typed text: prefix matches first, then word starts, then inside compounds.
  const matchIngredients = (q, limit = 4) => {
    if (!data) return [];
    const needle = norm(q).trim();
    if (needle.length < 2) return [];
    const found = [];
    data.cands.forEach((cand) => {
      if (!contains(cand.key, needle) && !cand.key.startsWith(needle)) return;
      const at = cand.key.indexOf(needle);
      const rank = at === 0 ? 0 : /[a-z]/.test(cand.key[at - 1]) ? 2 : 1;
      found.push({ cand, rank });
    });
    const out = [];
    found
      .map((entry) => ({ ...entry, count: countFor(entry.cand.key) }))
      .sort((a, b) => a.rank - b.rank || b.count - a.count || a.cand.key.length - b.cand.key.length)
      .forEach(({ cand, count }) => {
        if (out.length >= limit) return;
        // "Tofu fest Natur" adds nothing next to "Tofu" when it covers the same recipes.
        if (out.some((o) => cand.key.includes(o.key) && count <= o.count)) return;
        out.push({ key: cand.key, name: cand.name, count });
      });
    return out;
  };

  const popular = (names, min = 3) => {
    if (!data) return names.map((name) => ingredient(name));
    return names.map((name) => ingredient(name)).filter((ing) => ing.count >= min);
  };

  /* ---------------------------------------------------------------- categories (server-rendered JSON) */

  let cats = null;
  const categories = () => {
    if (cats) return cats;
    try {
      const el = document.getElementById('vhp-search-categories');
      const list = el ? JSON.parse(el.textContent || '[]') : [];
      const seen = new Set();
      cats = list.filter((cat) => cat && cat.handle && cat.title && !seen.has(cat.handle) && seen.add(cat.handle));
      cats.forEach((cat) => (cat.titleN = norm(cat.title)));
    } catch (_) {
      cats = [];
    }
    return cats;
  };

  const matchCategories = (q, limit = 3) => {
    const tokens = words(q).map(stem);
    if (!tokens.length || norm(q).trim().length < 2) return [];
    return categories()
      .map((cat) => {
        if (!tokens.every((t) => cat.titleN.includes(t))) return null;
        return { cat, rank: cat.titleN.startsWith(tokens[0]) ? 0 : 1 };
      })
      .filter(Boolean)
      .sort((a, b) => a.rank - b.rank || (b.cat.count || 0) - (a.cat.count || 0))
      .slice(0, limit)
      .map((entry) => entry.cat);
  };

  /* ---------------------------------------------------------------- filters + sorting */

  const kcalRange = (kcal) => {
    if (!kcal) return null;
    const [min, max] = String(kcal).split('-');
    return { min: min === '' ? 0 : Number(min), max: max === '' || max == null ? Infinity : Number(max) };
  };

  const numericFilters = (s) => {
    const out = [];
    if (s.protein) out.push(`protein_g>=${Number(s.protein)}`);
    if (s.time) out.push('zeit_min>0', `zeit_min<=${Number(s.time)}`);
    const range = kcalRange(s.kcal);
    if (range) {
      if (range.min > 0) out.push(`kcal>=${range.min}`);
      if (range.max !== Infinity) out.push(`kcal<${range.max}`);
    }
    return out;
  };

  const passes = (row, s) => {
    if (s.protein && !(row.protein >= s.protein)) return false;
    if (s.time && !(row.time > 0 && row.time <= s.time)) return false;
    const range = kcalRange(s.kcal);
    if (range && !(row.kcal != null && row.kcal >= range.min && row.kcal < range.max)) return false;
    return true;
  };

  const density = (row) => (row.protein && row.kcal ? row.protein / row.kcal : -1);
  const SORTERS = {
    protein: (a, b) => (b.protein ?? -1) - (a.protein ?? -1),
    density: (a, b) => density(b) - density(a),
    time: (a, b) => (a.time || Infinity) - (b.time || Infinity),
    kcal: (a, b) => (a.kcal ?? Infinity) - (b.kcal ?? Infinity),
    new: (a, b) => (b.pub || '').localeCompare(a.pub || ''),
  };
  const sortRows = (rows, sort) => (SORTERS[sort] ? rows.slice().sort(SORTERS[sort]) : rows);

  // Facet values starting with "-" would read as a negation.
  const facet = (raw) => `zutaten:${/^[-\\]/.test(raw) ? `\\${raw}` : raw}`;
  const groupFor = (key) => family(key).map(facet);

  /* ---------------------------------------------------------------- search */

  const cache = new Map();
  const remember = (key, promise) => {
    cache.set(key, promise);
    if (cache.size > MAX_CACHE) cache.delete(cache.keys().next().value);
    promise.catch(() => cache.delete(key));
    return promise;
  };

  const rowFor = (hit) => data.byId.get(String(hit.objectID)) || data.byHandle.get(hit.handle) || null;

  // A typed word that is an ingredient also searches as one (catches compounds).
  const splitQuery = (q) => {
    const typed = String(q || '').trim().split(/[\s,+]+/).filter(Boolean);
    const ingWords = [];
    const rest = [];
    typed.forEach((word) => {
      const key = stem(norm(word));
      if (key.length >= 3 && family(key).length) ingWords.push(key);
      else rest.push(word);
    });
    return { ingWords, rest: rest.join(' ') };
  };

  /**
   * state: { q, ings: [key], protein, time, kcal }
   * mode: 'exact' | 'similar' (typo fallback) | 'some' (not every ingredient; have = Map id -> matched count)
   */
  const search = (state) => {
    const s = { q: String(state.q || '').trim(), ings: (state.ings || []).map((k) => norm(k).trim()).filter(Boolean), protein: state.protein || 0, time: state.time || 0, kcal: state.kcal || '' };
    const key = JSON.stringify(s);
    if (cache.has(key)) return cache.get(key);
    const job = loadData().then(async () => {
      const filters = numericFilters(s);
      const ingGroups = s.ings.map(groupFor).filter((g) => g.length);
      const base = { hitsPerPage: 1000, attributesToRetrieve: ['handle'], attributesToHighlight: [], numericFilters: filters };
      const requests = [{ ...base, query: s.q, facetFilters: ingGroups }];
      if (s.q) {
        const { ingWords, rest } = splitQuery(s.q);
        if (ingWords.length) requests.push({ ...base, query: rest, facetFilters: ingGroups.concat(ingWords.map(groupFor)) });
      }
      const results = await multi(requests);
      const seen = new Set();
      const rows = [];
      results.forEach((result) =>
        (result.hits || []).forEach((hit) => {
          const row = rowFor(hit);
          if (row && !seen.has(row.id)) {
            seen.add(row.id);
            rows.push(row);
          }
        })
      );
      if (rows.length) return { rows, mode: 'exact' };

      // Several ingredients, no recipe with all of them: the ones with the most of them.
      if (s.ings.length > 1) {
        const [any] = await multi([{ ...base, query: s.q, facetFilters: [ingGroups.flat()] }]);
        const have = new Map();
        const some = (any.hits || []).map(rowFor).filter(Boolean);
        some.forEach((row) => have.set(row.id, s.ings.filter((k) => row.zn.some((n) => contains(n, k))).length));
        some.sort((a, b) => have.get(b.id) - have.get(a.id));
        // With two or more of them in reach, recipes with just one are noise.
        const best = some.length ? have.get(some[0].id) : 0;
        const kept = best > 1 ? some.filter((row) => have.get(row.id) > 1) : some;
        if (kept.length) return { rows: kept, mode: 'some', have };
      }
      // Nothing for the exact words: let Algolia drop words.
      if (s.q) {
        const [loose] = await multi([{ ...base, query: s.q, facetFilters: ingGroups, removeWordsIfNoResults: 'allOptional' }]);
        const similar = (loose.hits || []).map(rowFor).filter(Boolean);
        if (similar.length) return { rows: similar, mode: 'similar' };
      }
      return { rows: [], mode: 'exact' };
    });
    return remember(key, job);
  };

  // Typeahead: Algolia's top recipes (prefix search on the last word) + local ingredient / category rows.
  const suggestCache = new Map();
  const suggest = (q) => {
    const text = String(q || '').trim();
    const key = norm(text);
    if (!suggestCache.has(key)) {
      const job = loadData().then(async () => {
        const { ingWords, rest } = splitQuery(text);
        const requests = [{ query: text, hitsPerPage: 6, attributesToRetrieve: ['handle'], attributesToHighlight: [] }];
        if (ingWords.length) {
          requests.push({ query: rest, hitsPerPage: 6, attributesToRetrieve: ['handle'], attributesToHighlight: [], facetFilters: ingWords.map(groupFor) });
        }
        const results = await multi(requests);
        const seen = new Set();
        const recipes = [];
        results.forEach((result) =>
          (result.hits || []).forEach((hit) => {
            const row = rowFor(hit);
            if (row && !seen.has(row.id) && recipes.length < 6) {
              seen.add(row.id);
              recipes.push(row);
            }
          })
        );
        return { recipes, ingredients: matchIngredients(text), categories: matchCategories(text) };
      });
      suggestCache.set(key, job);
      job.catch(() => suggestCache.delete(key));
      if (suggestCache.size > MAX_CACHE) suggestCache.delete(suggestCache.keys().next().value);
    }
    return suggestCache.get(key);
  };

  /* ---------------------------------------------------------------- planner: ids for a query */

  const idCache = new Map();
  const ids = (q) => {
    const key = norm(q).trim();
    if (!key) return Promise.resolve(null);
    return search({ q }).then((result) => {
      const rank = new Map();
      result.rows.forEach((row, i) => row.pid && rank.set(row.pid, i));
      idCache.set(key, rank);
      return rank;
    });
  };
  const peekIds = (q) => idCache.get(norm(q).trim()) || null;

  /* ---------------------------------------------------------------- recent searches */

  // Entry: { t: 'q' | 'ing' | 'cat' | 'rec', k: key, label, sub?, img?, h?: handle }
  const readRecent = () => {
    try {
      const list = JSON.parse(localStorage.getItem(RECENT_KEY) || 'null');
      if (Array.isArray(list)) return list.filter((e) => e && e.t && e.k && e.label).slice(0, MAX_RECENT);
      const legacy = JSON.parse(localStorage.getItem(LEGACY_RECENT_KEY) || '[]');
      return Array.isArray(legacy)
        ? legacy.filter((q) => typeof q === 'string' && q.trim()).map((q) => ({ t: 'q', k: norm(q).trim(), label: q.trim() }))
        : [];
    } catch (_) {
      return [];
    }
  };
  const writeRecent = (list) => {
    try {
      localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, MAX_RECENT)));
      localStorage.removeItem(LEGACY_RECENT_KEY);
    } catch (_) {}
  };
  const recents = {
    list: readRecent,
    add(entry) {
      if (!entry || !entry.k || !entry.label) return;
      if (entry.t === 'q' && String(entry.label).trim().length < 2) return;
      writeRecent([entry].concat(readRecent().filter((e) => !(e.t === entry.t && e.k === entry.k))));
    },
    remove(t, k) {
      writeRecent(readRecent().filter((e) => !(e.t === t && e.k === k)));
    },
    clear() {
      writeRecent([]);
    },
  };

  window.vhpSearchCore = {
    norm,
    stem,
    load: loadData,
    data: () => data,
    suggest,
    search,
    ids,
    peekIds,
    ingredient,
    matchIngredients,
    popular,
    categories,
    matchCategories,
    kcalRange,
    passes,
    sortRows,
    recents,
  };
})();
